import { useEffect, useState } from 'react';

interface DiagnosticResults {
  elementsWithDataLovablePath: number;
  elementsWithDataLovableId: number;
  scriptLoaded: boolean;
  sampleH1Attributes: {
    'data-lovable-path'?: string;
    'data-lovable-id'?: string;
  } | null;
  allH1Attributes: string[];
  firstElementWithPath: string | null;
}

export default function LovableDebug() {
  const [results, setResults] = useState<DiagnosticResults>({
    elementsWithDataLovablePath: 0,
    elementsWithDataLovableId: 0,
    scriptLoaded: false,
    sampleH1Attributes: null,
    allH1Attributes: [],
    firstElementWithPath: null,
  });

  useEffect(() => {
    // Run diagnostics after component mounts
    const runDiagnostics = () => {
      // Count elements with data-lovable-path
      const elementsWithPath = document.querySelectorAll('[data-lovable-path]');

      // Count elements with data-lovable-id
      const elementsWithId = document.querySelectorAll('[data-lovable-id]');

      // Check if gptengineer.js script is loaded
      const scriptLoaded = Array.from(document.scripts).some((script) =>
        script.src.includes('gptengineer.js')
      );

      // Get sample attributes from h1 element
      const h1Element = document.querySelector('h1');
      const sampleH1Attributes = h1Element
        ? {
            'data-lovable-path': h1Element.getAttribute('data-lovable-path') || undefined,
            'data-lovable-id': h1Element.getAttribute('data-lovable-id') || undefined,
          }
        : null;

      // Get ALL attributes from h1 element for debugging
      const allH1Attributes = h1Element
        ? Array.from(h1Element.attributes).map((attr) => `${attr.name}="${attr.value}"`)
        : [];

      // Get first element with path attribute for inspection
      const firstElement = elementsWithPath[0];
      const firstElementWithPath = firstElement
        ? `<${firstElement.tagName.toLowerCase()} data-lovable-path="${firstElement.getAttribute('data-lovable-path')}">`
        : null;

      const diagnosticResults = {
        elementsWithDataLovablePath: elementsWithPath.length,
        elementsWithDataLovableId: elementsWithId.length,
        scriptLoaded,
        sampleH1Attributes,
        allH1Attributes,
        firstElementWithPath,
      };

      setResults(diagnosticResults);

      // Enhanced console logging
      console.log('=== Lovable Visual Edits Diagnostics ===');
      console.log('Elements with data-lovable-path:', elementsWithPath.length);
      console.log('Elements with data-lovable-id:', elementsWithId.length);
      console.log('gptengineer.js script loaded:', scriptLoaded);
      console.log('Sample h1 attributes:', sampleH1Attributes);
      console.log('ALL h1 attributes:', allH1Attributes);
      console.log('First element with path:', firstElement);
      console.log('First element HTML:', firstElementWithPath);
      console.log('======================================');
    };

    // Run diagnostics immediately
    runDiagnostics();

    // Run again after a short delay to catch dynamically added elements
    const timer = setTimeout(runDiagnostics, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="container mx-auto max-w-4xl p-8">
      <h1 className="mb-8 text-4xl font-bold">Lovable Visual Edits Diagnostics</h1>

      <div className="space-y-6">
        {/* Diagnostic Results */}
        <section className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
          <h2 className="mb-4 text-2xl font-semibold">Diagnostic Results</h2>

          <div className="space-y-3">
            <div className="flex items-center justify-between rounded bg-gray-50 p-3 dark:bg-gray-700">
              <span className="font-medium">Elements with data-lovable-path:</span>
              <span className="text-xl">
                {results.elementsWithDataLovablePath > 0 ? '✅' : '❌'}{' '}
                {results.elementsWithDataLovablePath}
              </span>
            </div>

            <div className="flex items-center justify-between rounded bg-gray-50 p-3 dark:bg-gray-700">
              <span className="font-medium">Elements with data-lovable-id:</span>
              <span className="text-xl">
                {results.elementsWithDataLovableId > 0 ? '✅' : '❌'}{' '}
                {results.elementsWithDataLovableId}
              </span>
            </div>

            <div className="flex items-center justify-between rounded bg-gray-50 p-3 dark:bg-gray-700">
              <span className="font-medium">gptengineer.js script loaded:</span>
              <span className="text-xl">
                {results.scriptLoaded ? '✅' : '❌'} {results.scriptLoaded ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </section>

        {/* Sample H1 Attributes */}
        <section className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
          <h2 className="mb-4 text-2xl font-semibold">Sample H1 Attributes</h2>

          {results.sampleH1Attributes ? (
            <div className="space-y-2">
              {results.sampleH1Attributes['data-lovable-path'] ? (
                <div className="rounded bg-green-50 p-3 dark:bg-green-900/20">
                  <span className="font-medium">data-lovable-path:</span>{' '}
                  <code className="text-sm">{results.sampleH1Attributes['data-lovable-path']}</code>
                </div>
              ) : (
                <div className="rounded bg-red-50 p-3 dark:bg-red-900/20">
                  <span className="font-medium">data-lovable-path:</span> Not found
                </div>
              )}

              {results.sampleH1Attributes['data-lovable-id'] ? (
                <div className="rounded bg-green-50 p-3 dark:bg-green-900/20">
                  <span className="font-medium">data-lovable-id:</span>{' '}
                  <code className="text-sm">{results.sampleH1Attributes['data-lovable-id']}</code>
                </div>
              ) : (
                <div className="rounded bg-red-50 p-3 dark:bg-red-900/20">
                  <span className="font-medium">data-lovable-id:</span> Not found
                </div>
              )}
            </div>
          ) : (
            <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
              <p className="text-gray-600 dark:text-gray-400">No h1 element found on page</p>
            </div>
          )}
        </section>

        {/* Enhanced Diagnostics */}
        <section className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
          <h2 className="mb-4 text-2xl font-semibold">Enhanced Diagnostics</h2>

          <div className="space-y-4">
            <div>
              <h3 className="mb-2 font-semibold">All H1 Attributes:</h3>
              {results.allH1Attributes.length > 0 ? (
                <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                  <code className="block whitespace-pre-wrap break-all text-xs">
                    {results.allH1Attributes.join('\n')}
                  </code>
                </div>
              ) : (
                <div className="rounded bg-yellow-50 p-3 dark:bg-yellow-900/20">
                  <p className="text-sm text-gray-600 dark:text-gray-400">No h1 element found</p>
                </div>
              )}
            </div>

            <div>
              <h3 className="mb-2 font-semibold">First Element with data-lovable-path:</h3>
              {results.firstElementWithPath ? (
                <div className="rounded bg-green-50 p-3 dark:bg-green-900/20">
                  <code className="block break-all text-xs">{results.firstElementWithPath}</code>
                </div>
              ) : (
                <div className="rounded bg-red-50 p-3 dark:bg-red-900/20">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    ❌ No elements found with data-lovable-path attribute
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Test Elements */}
        <section className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
          <h2 className="mb-4 text-2xl font-semibold">Test Elements for Visual Edits</h2>
          <p className="mb-4 text-gray-600 dark:text-gray-400">
            These elements should be selectable in Lovable Visual Edits:
          </p>

          <div className="space-y-4">
            <div className="rounded border-2 border-dashed border-blue-300 p-4">
              <h3 className="mb-2 text-xl font-semibold">Test Heading</h3>
              <p className="text-gray-700 dark:text-gray-300">
                This is a test paragraph that should be editable in Lovable Visual Edits.
              </p>
            </div>

            <div className="rounded border-2 border-dashed border-green-300 p-4">
              <button className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600">
                Test Button
              </button>
            </div>

            <div className="rounded border-2 border-dashed border-purple-300 p-4">
              <div className="flex gap-4">
                <div className="h-24 w-24 rounded-lg bg-gradient-to-br from-blue-400 to-purple-500"></div>
                <div className="flex-1">
                  <h4 className="mb-1 font-semibold">Test Card</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Card content for testing selection
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Instructions */}
        <section className="rounded-lg bg-blue-50 p-6 dark:bg-blue-900/20">
          <h2 className="mb-4 text-2xl font-semibold">Instructions</h2>
          <ul className="list-inside list-disc space-y-2 text-gray-700 dark:text-gray-300">
            <li>Check the diagnostic results above to see if attributes are present</li>
            <li>All counts should show ✅ with non-zero values</li>
            <li>gptengineer.js script should be loaded (✅)</li>
            <li>Sample H1 attributes should show valid paths</li>
            <li>Try selecting the test elements below in Lovable Visual Edits</li>
            <li>Check browser console for detailed diagnostic logs</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
